import React, { useEffect } from 'react';
import router from 'next/router';
import { shallow } from 'zustand/shallow';

import { BookmarkIcon, Button } from '@motorway/mw-highway-code';
import { Chip } from '@motorway/mw-highway-code/pre-alpha';

import cypressIds from 'Components/cypress_ids.json';
import texts from 'Components/shared/SavedFilters/SavedFilters.texts.json';
import { setIsCurrentFilterUnique } from 'Components/shared/SavedFilters/useSetIsUniqueFilter';
import { triggerAddSavedSearchButtonClickEvent } from 'Services/analytics/events';
import { useFilters } from 'Stores/FilterStore/VehicleListFilter/VehicleListFilterStore';
import { MAX_NUMBER_OF_ALLOWED_SEARCHES_REACHED } from 'Stores/SavedFiltersStore/SavedFilters.conf';
import useSavedFiltersStore, {
	useIsUniqueFilterInStore,
	useSetIsUniqueFilterInStore,
} from 'Stores/SavedFiltersStore/SavedFiltersStore';
import { INTENT_TYPES } from 'Utilities/FormBase/FormBase.helpers';
import { applyCypressData } from 'Utilities/helpers';
import ROUTES from 'Utilities/routes';

import { triggerSavedFilterLimitReachedGAEvent } from '../SavedFilters.analytics';

interface SavedFiltersButtonProps {
	saveChipText?: string;
}

const { href } = ROUTES.MANAGE_SAVED_SEARCH_ADD;

const SavedFiltersButton: React.FC<SavedFiltersButtonProps> = ({ saveChipText }: SavedFiltersButtonProps) => {
	const { maxFilters, savedFilters, setError } = useSavedFiltersStore(
		(store) => ({
			maxFilters: store?.maxFilters,
			savedFilters: store?.filters,
			setError: store?.setError,
		}),
		shallow,
	);
	const vehicleFilters = useFilters();

	const isMaxFiltersReached = savedFilters.length >= (maxFilters as unknown as number);
	const isUniqueFilter = useIsUniqueFilterInStore();
	const setIsUniqueFilter = useSetIsUniqueFilterInStore();

	const onSave = () => {
		triggerAddSavedSearchButtonClickEvent();
		if (isMaxFiltersReached) {
			triggerSavedFilterLimitReachedGAEvent();
			setError({
				intent: INTENT_TYPES.WARNING,
				message: MAX_NUMBER_OF_ALLOWED_SEARCHES_REACHED,
			});
			return;
		}

		void router.push(href);
	};

	useEffect(() => {
		setIsCurrentFilterUnique(vehicleFilters, savedFilters, setIsUniqueFilter);
	}, [vehicleFilters, savedFilters, setIsUniqueFilter]);

	const sharedProps = {
		...applyCypressData(cypressIds.inputs.saveNewFilter),
		'aria-label': texts.savedFiltersButtonAriaLabel,
		disabled: !isUniqueFilter,
		icon: () => <BookmarkIcon filled={false} />,
		onClick: onSave,
	};

	if (!saveChipText) {
		return (
			<Button
				{...sharedProps}
				label={texts.savedFiltersButtonAriaLabel}
				showLabel={false}
				size="small"
				variant="secondary"
			/>
		);
	}

	return <Chip {...sharedProps} reverse label={saveChipText} />;
};

export default SavedFiltersButton;
